import { useRef } from "react";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api, { formatApiError } from "../../lib/api";

/**
 * v4.6.3 — PayPal checkout panel. The reservation is persisted first, then
 * the order is created AND captured server-side from the stored booking
 * total, so the browser can never dictate the charged amount.
 */
export default function PayPalPanel({ clientId, mode, disabled, amountLabel, ensureBooking, onPaid }) {
  const bookingRef = useRef(null);

  if (disabled) {
    return (
      <p className="text-xs text-neutral-500 mt-4" data-testid="pb-paypal-blocked">
        Accept the Rental Terms &amp; Conditions to pay with PayPal.
      </p>
    );
  }

  return (
    <div className="mt-4" data-testid="pb-paypal-panel">
      <p className="text-[11px] text-neutral-500 mb-2">
        You will be charged {amountLabel} through PayPal.
        {mode === "sandbox" && " (sandbox mode)"}
      </p>
      <PayPalScriptProvider options={{ clientId, currency: "EUR", intent: "capture" }}>
        <PayPalButtons
          style={{ layout: "vertical", height: 44, shape: "rect", label: "paypal" }}
          createOrder={async () => {
            const booking = bookingRef.current || await ensureBooking();
            if (!booking?.id) throw new Error("Booking could not be created");
            bookingRef.current = booking;
            const { data } = await api.post("/public/paypal/order", { booking_id: booking.id });
            return data.id;
          }}
          onApprove={async (data) => {
            const { data: booking } = await api.post("/public/paypal/capture", {
              booking_id: bookingRef.current?.id,
              order_id:   data.orderID,
            });
            onPaid(booking);
          }}
          onError={(err) => {
            toast.error(formatApiError(err?.response?.data?.detail) || "PayPal payment failed — please try again.");
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
}
